/**
 * English dictionary.
 *
 * Mirrors `ru.ts` key for key. A missing key falls back to Russian
 * rather than showing the key itself: a Russian line in an English
 * interface still tells the reader something, `feed.title` does not.
 *
 * Terminology follows what traders actually say in English, not literal
 * translation. «Правило» is a rule, «срабатывание» is a trigger, and a
 * signal from a rule is an alert — it states nothing about direction.
 */

export const en: Record<string, string> = {
  // --- common
  'common.close': 'Close',
  'common.back': 'Back',
  'common.all': 'All',
  'common.save': 'Save',
  'common.delete': 'Delete',
  'common.cancel': 'Cancel',
  'common.none': '—',

  // --- top bar
  'top.indicators': 'Indicators',
  'top.rules': 'Rules',
  'top.bots': 'Bots',
  'top.settings': 'Settings',
  'top.connection.connecting': 'connecting',
  'top.connection.open': 'live',
  'top.connection.closed': 'offline',

  // --- signal feed
  'feed.title': 'Signals',
  'feed.source.all': 'All',
  'feed.source.rules': 'Indicators',
  'feed.source.bots': 'Bots',
  'feed.side.all': 'All',
  'feed.allSources': 'All sources',
  'feed.currentTimeframe': 'Current timeframe only',
  'feed.empty': 'No signals yet',
  'feed.filtered': 'All signals filtered out by filters and thresholds',
  'feed.event': 'ALERT',
  'feed.disconnected': '{name} (disconnected)',

  // --- signal card
  'card.source': 'Source',
  'card.source.rule': 'Your rule',
  'card.source.bot': 'Bot · {version}',
  'card.event': 'Alert',
  'card.symbol': 'Symbol',
  'card.timeframe': 'Timeframe',
  'card.price': 'Price',
  'card.time': 'Time',
  'card.status': 'Status',
  'card.details': 'Details',
  'card.targets': 'Targets',
  'card.stop': 'Stop',
  'card.rule.conditions': 'Conditions at the time · v{version}',
  'card.rule.closed': 'Bar closed at {time} UTC',
  'card.rule.origin.history':
    'Historical mark: this is where the rule would have fired. No notification was sent.',
  'card.rule.origin.live': 'This happened live: the bar closed and the rule fired.',
  'card.rule.open': 'Open the rule on the chart',

  // --- rules
  'rules.title': 'Rules',
  'rules.new': '+ New rule',
  'rules.empty':
    'A rule describes what has to happen on the market for you to hear about it. No coding needed: conditions are built from ready-made values.',
  'rules.disabled': 'disabled',
  'rules.otherInstrument': 'another symbol',
  'rules.when': 'When',
  'rules.instrument': 'Symbol',
  'rules.fires': 'Fires',
  'rules.fires.onClose': 'on bar close',
  'rules.version': 'Version',
  'rules.history': 'History',
  'rules.history.needInstrument': 'open this symbol to count',
  'rules.settings': 'Settings',
  'rules.showOnChart': 'Show on the chart',
  'rules.showOnChart.hint': 'A disabled rule is neither evaluated nor notified about.',
  'rules.notify': 'Notify me',
  'rules.notify.hint':
    'Arrives once the bar closes and the signal is confirmed — even if ANZONIX is closed.',
  'rules.edit': 'Edit',
  'rules.remove': 'Delete rule',

  // --- builder
  'builder.name': 'Rule name',
  'builder.when': 'When',
  'builder.addCondition': '+ Condition',
  'builder.addGroup': '+ Group',
  'builder.and': 'AND',
  'builder.or': 'OR',
  'builder.onTimeframe': 'on',
  'builder.sameTimeframe': '{tf} — same as the rule',
  'builder.check': 'Check against history',
  'builder.checkTitle': 'History check',
  'builder.collapse': 'Collapse',
  'builder.whatItDoes': 'What this rule does',
  'builder.closedBarNote':
    'A signal appears only after the bar closes. Before that the condition may hold and stop holding.',
  'builder.pending': 'The condition holds right now, but the bar has not closed yet.',
  'builder.zero':
    'Over the shown period ({bars} bars) this never happened. Try relaxing the condition — raising the threshold, for instance.',
  'builder.snapshot.changed':
    'The rule has changed since. This is how it looked when the signal appeared.',
  'builder.snapshot.deleted':
    'The rule was deleted. This is how it looked when the signal appeared.',
  'builder.snapshot.conditions': 'Conditions at that moment',
  'builder.snapshot.current': 'Show the current version (v{version})',

  // --- operands
  'operand.pick': 'Value',
  'operand.search': 'Search: RSI, volume, trend…',
  'operand.notFound': 'Nothing found',
  'operand.multiply': 'Multiply by a number',
  'operand.multiply.remove': 'Remove multiplier',
  'operand.shift': 'Take the value bars ago',
  'operand.shift.remove': 'Remove the shift',
  'operand.back': 'ago',

  // --- bots
  'bots.title': 'Bots',
  'bots.connected': 'Connected',
  'bots.empty':
    'No bot is connected. A bot is a separate program: it runs in its own process and connects to ANZONIX itself.',
  'bots.empty.run': 'Run the reference one: {command}',
  'bots.empty.rules':
    'If you want your own conditions without programming, that is not a bot but a rule — create it under Rules.',
  'bots.note': 'A bot connects on its own, as a separate process: {command}. The terminal shows those that arrived.',
  'bots.noStreams': 'no streams',
  'bots.andMore': 'and {count} more',
  'bots.hidden': 'hidden from the feed',
  'bots.params': 'Parameters',
  'bots.noParams': 'This bot declares no configurable parameters',
  'bots.inFeed': 'In the feed',
  'bots.showSignals': 'Show signals from this bot',
  'bots.showSignals.hint':
    'This is about the feed and the chart. The bot keeps running either way.',
  'bots.stop': 'Stop',
  'bots.start': 'Start',
  'bots.resetParams': 'Reset parameters',
  'bots.status.RUNNING': 'Running',
  'bots.status.WARMING_UP': 'Warming up',
  'bots.status.DEGRADED': 'Degraded',
  'bots.status.STOPPED': 'Stopped',
  'bots.status.ERROR': 'Error',

  // --- settings
  'settings.title': 'Settings',
  'settings.tab.appearance': 'Interface',
  'settings.tab.notifications': 'Notifications',
  'settings.tab.thresholds': 'Thresholds',
  'settings.tab.system': 'System',
  'settings.tab.about': 'About',
  'settings.theme': 'Theme',
  'settings.theme.system': 'Match system',
  'settings.theme.dark': 'Dark',
  'settings.theme.light': 'Light',
  'settings.language': 'Language',
  'settings.language.system': 'Match system',
  'settings.language.ru': 'Русский',
  'settings.language.en': 'English',
  'settings.serverTexts':
    'Market Intelligence verdicts and rule explanations are generated on the server and remain in Russian for now.',
  'settings.sync.synced': 'saved',
  'settings.sync.saving': 'saving…',
  'settings.sync.offline': 'local',
  'settings.sync.loading': 'loading…',
  'settings.system.connection': 'Connection',
  'settings.system.stream': 'Data stream',
  'settings.system.profile': 'Profile',
  'settings.system.venue': 'Venue',
  'settings.system.core': 'Server',
  'settings.system.coreDown': 'The server is not responding. The chart and the feed show what was loaded.',
  'settings.system.venues': 'Venues',
  'settings.system.subscribers': 'Subscribers',
  'settings.system.signals': 'Signals accepted',
  'settings.system.bots': 'Bots connected',
  'settings.system.push': 'Push sent',
  'settings.system.pushOff': 'channel off',
};
